// What the site tells the tag container, when there is one: a handful of named events on
// the dataLayer, which gtm turns into ga4 events once the viewer has consented. Without a
// container the array simply fills and nothing reads it. Events and their fields are the
// contract with docs/telemetry/gtm_workspace.json: change one, change the other.

export function track(event: string, data: Record<string, unknown> = {}) {
  (window.dataLayer = window.dataLayer || []).push({ event, ...data });
}

/**
 * Push authenticated user state to dataLayer on page load if present on root.
 */
export function initUserId() {
  if (typeof document !== "undefined" && document.documentElement?.dataset?.userId) {
    (window.dataLayer = window.dataLayer || []).push({ user_id: document.documentElement.dataset.userId });
  }
}

/**
 * Determine `via` for bout_open: 'list' | 'random' | 'live' | 'follow' | 'share'
 */
export function extractBoutVia(
  loc: Location = window.location,
  doc: Document = document,
  hist: History = window.history,
): "list" | "random" | "live" | "follow" | "share" {
  if (loc && loc.search) {
    const params = new URLSearchParams(loc.search);
    const via = params.get("via");
    if (via === "list" || via === "random" || via === "live" || via === "follow" || via === "share") {
      return via;
    }
    if (params.get("random") === "1") {
      return "random";
    }
  }

  if (hist?.state && typeof hist.state.via === "string") {
    const v = hist.state.via;
    if (v === "list" || v === "random" || v === "live" || v === "follow" || v === "share") {
      return v;
    }
  }

  if (doc?.referrer) {
    try {
      const ref = new URL(doc.referrer);
      const isSameOrigin = loc?.origin ? ref.origin === loc.origin : true;
      if (isSameOrigin) {
        const path = ref.pathname;
        if (path === "/" || path === "/tv") return "live";
        if (path.startsWith("/bouts") || path.startsWith("/banzuke") || path.startsWith("/contender/")) return "list";
        if (path.startsWith("/u/")) return "follow";
      }
    } catch (_) {}
  }

  return "share";
}

/**
 * Track bout_open macro event.
 */
export function trackBoutOpen(
  boutElement: HTMLElement | null = typeof document !== "undefined" ? document.querySelector("article.bout") : null,
) {
  if (!boutElement || boutElement.dataset.boutOpened) return;
  boutElement.dataset.boutOpened = "1";
  track("bout_open", { via: extractBoutVia() });
}

/**
 * Track contender submission funnel events.
 */
export function trackEnterFunnel(
  stage: "start" | "submit" | "fail",
  details: { step?: string; error_code?: string; provider_kind?: string } = {},
) {
  if (stage === "start") {
    track("enter_start");
  } else if (stage === "submit") {
    track("enter_submit", { provider_kind: details.provider_kind || "auto" });
  } else if (stage === "fail") {
    track("enter_fail", { step: details.step || "unknown", error_code: details.error_code || "unknown" });
  }
}

/** The bout being watched and since when, for `bout_dwell`. */
let dwell: { bout: HTMLElement; at: number } | null = null;
/** The page's leaving is listened for once. */
let listening = false;

/**
 * Start timing a bout on the page (once per article): `bout_dwell` goes out when it is left, by navigation, by
 * closing the page, or by the tv swapping in the next bout.
 */
export function startBoutDwell(bout: HTMLElement | null, now: () => number = Date.now) {
  if (!bout || dwell?.bout === bout) return;
  endBoutDwell(now);
  dwell = { bout, at: now() };
  if (typeof window !== "undefined" && !listening) {
    listening = true;
    window.addEventListener("pagehide", () => endBoutDwell());
  }
}

/** The bout was left: how long it was watched, how many turns were on screen, and whether it had ended. */
export function endBoutDwell(now: () => number = Date.now) {
  if (!dwell) return;
  const { bout, at } = dwell;
  dwell = null;
  const turns = Array.from(bout.querySelectorAll("[data-transcript] > li[data-turn]")).filter(
    (li) => !(li as HTMLElement).hidden,
  ).length;
  const finished = Boolean(bout.dataset.verdict) || (bout.dataset.done === "1" && !bout.dataset.replay);
  track("bout_dwell", { seconds: Math.max(0, Math.round((now() - at) / 1000)), turns_seen: turns, finished });
}

/**
 * Track social action event.
 */
export function trackSocialAction(
  action: "follow" | "challenge" | "rematch" | "share",
  target_model: string | null = null,
) {
  track("social_action", { action, target_model });
}

/**
 * Send scoped first-party lifecycle interaction to POST /i.
 * Uses sendBeacon if available, otherwise fetch.
 */
export function sendInteraction(event: string, payload: Record<string, unknown> = {}) {
  const body = JSON.stringify({ event, payload });
  if (typeof navigator !== "undefined" && navigator.sendBeacon) {
    const blob = new Blob([body], { type: "application/json" });
    if (navigator.sendBeacon("/i", blob)) return;
  }
  if (typeof fetch !== "undefined") {
    fetch("/i", {
      method: "POST",
      body,
      headers: { "content-type": "application/json" },
      credentials: "same-origin",
      keepalive: true,
    }).catch(() => {});
  }
}

// Automatically push user_id if present on page load
initUserId();
